import React, { useState } from "react";
import { Modal, Checkbox } from "antd";
import {
  CascadeEntity,
  CascadeExclusion,
  CascadeRename,
  ConfirmedRename,
  TitleCascadePreview,
} from "../../../../services/courses/courses.service";

// Copy mirrors emmi-frontend's title-cascade-modal.tsx verbatim so both
// clients say the same thing to admins (SCRUM-797 BO-2).
export const CASCADE_ENTITY_LABEL: Record<CascadeEntity, string> = {
  lesson: "Lección",
  resource: "Recurso",
};

const cascadeExclusionReasonLabel = (exclusion: CascadeExclusion): string => {
  if (exclusion.reason === "shared_across_courses") {
    return exclusion.course_count
      ? `Se usa en ${exclusion.course_count} cursos distintos, así que no se renombra automáticamente.`
      : "Se usa en más de un curso, así que no se renombra automáticamente.";
  }
  return "Su título no coincide con el título anterior del curso.";
};

const renameRowKey = (rename: CascadeRename): string =>
  `${rename.entity}-${rename.id}`;

interface CascadeConfirmModalProps {
  open: boolean;
  preview: TitleCascadePreview;
  confirmLoading?: boolean;
  onConfirm: (renames: ConfirmedRename[]) => void;
  onCancel: () => void;
}

/**
 * Controlled Modal (not the imperative `Modal.confirm` API): rows need
 * reactive per-row checkbox state and a reactively-disabled OK button, which
 * a plain-string/one-shot confirm config cannot host. AntD's controlled
 * `<Modal open>` already renders fine under React 19 in this repo (see
 * `DeleteUserConfirmModal.tsx`) — only the static `Modal.confirm` API is
 * broken here, not the controlled component.
 *
 * Rows start checked so the common "confirm everything" case stays a single
 * click; unchecking a row drops it from the confirm payload without
 * affecting the others. Exclusions are server-refused and not user-decidable,
 * so they never get a checkbox.
 */
const CascadeConfirmModal: React.FC<CascadeConfirmModalProps> = ({
  open,
  preview,
  confirmLoading = false,
  onConfirm,
  onCancel,
}) => {
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(
    () => new Set(preview.renames.map(renameRowKey)),
  );
  const hasSelection = checkedKeys.size > 0;

  const toggleRow = (key: string) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <Modal
      open={open}
      title="Confirmar cambios relacionados"
      width={520}
      okText="Confirmar cambios"
      cancelText="Cancelar"
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !hasSelection }}
      onCancel={onCancel}
      onOk={() =>
        onConfirm(
          preview.renames
            .filter((rename) => checkedKeys.has(renameRowKey(rename)))
            .map((rename) => ({
              entity: rename.entity,
              id: rename.id,
              expected_current_title: rename.current_title,
              new_title: rename.new_title,
            })),
        )
      }
    >
      <p>
        Al renombrar el curso se detectaron lecciones y recursos con títulos
        relacionados. Revisa los cambios antes de confirmarlos.
      </p>
      <div
        data-testid="cascade-renames"
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        {preview.renames.map((rename) => {
          const key = renameRowKey(rename);
          return (
            <div key={key} data-testid={`cascade-row-${key}`}>
              <Checkbox
                checked={checkedKeys.has(key)}
                onChange={() => toggleRow(key)}
              >
                {CASCADE_ENTITY_LABEL[rename.entity]}: {rename.current_title} →{" "}
                {rename.new_title}
              </Checkbox>
            </div>
          );
        })}
      </div>
      {preview.excluded.length > 0 && (
        <div data-testid="cascade-excluded" style={{ marginTop: 16 }}>
          <strong>No se renombrarán:</strong>
          {preview.excluded.map((exclusion) => (
            <div
              key={`${exclusion.entity}-${exclusion.id}`}
              style={{ fontSize: 12 }}
            >
              {CASCADE_ENTITY_LABEL[exclusion.entity]} &ldquo;{exclusion.title}
              &rdquo; — {cascadeExclusionReasonLabel(exclusion)}
            </div>
          ))}
        </div>
      )}
      {hasSelection === false && (
        <p
          data-testid="cascade-none-selected"
          style={{ color: "#ff4d4f", fontSize: 12, marginTop: 8 }}
        >
          Selecciona al menos una fila para confirmar los cambios.
        </p>
      )}
    </Modal>
  );
};

export default CascadeConfirmModal;
